import { Archive } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardContent, CardHeader } from "@/components/ui/card";
import type { LaunchCampaignSummary } from "@/lib/core/schemas/launch-hub";
import { cn } from "@/lib/utils";

// See character-card.tsx for why this reproduces Card's className on a
// native <article> instead of using the Card primitive's role="group" div.
const CARD_CLASSNAME =
  "rounded-lg border bg-card text-card-foreground shadow-sm";

type CampaignCardProps = {
  campaign: LaunchCampaignSummary;
  canArchive: boolean;
  archiveDisabledReason: string | null;
  onArchive: (campaign: LaunchCampaignSummary) => void;
};

/** Active campaigns only — archived ones render via ArchivedCampaignCard. */
export function CampaignCard({
  campaign,
  canArchive,
  archiveDisabledReason,
  onArchive,
}: CampaignCardProps) {
  const characterNames = campaign.assignedCharacters
    .map((c) => c.characterName)
    .join(", ");

  return (
    <article
      aria-label={campaign.name}
      className={cn(CARD_CLASSNAME, "flex h-full flex-col")}
    >
      <Link className="flex flex-1 flex-col" to={`/campaigns/${campaign.id}`}>
        <CardHeader className="space-y-1.5 p-4">
          <h3 className="font-semibold text-base leading-tight">
            {campaign.name}
          </h3>
          <p className="text-muted-foreground text-xs">
            Created {campaign.createdAt.toLocaleDateString()}
          </p>
          <p className="text-muted-foreground text-sm">
            {characterNames || "No characters assigned"}
          </p>
        </CardHeader>
      </Link>
      <CardContent className="mt-auto flex flex-col items-end gap-1 p-4 pt-0">
        <Button
          aria-label={`Archive ${campaign.name}`}
          disabled={!canArchive}
          onClick={() => onArchive(campaign)}
          size="icon"
          type="button"
          variant="outline"
        >
          <Archive className="h-4 w-4" />
        </Button>
        {!canArchive && archiveDisabledReason && (
          <p className="text-muted-foreground text-xs">
            {archiveDisabledReason}
          </p>
        )}
      </CardContent>
    </article>
  );
}

type ArchivedCampaignCardProps = {
  campaign: LaunchCampaignSummary;
};

/**
 * Archived campaigns are visible but not clickable/openable — no <Link>,
 * visually muted, explicitly labelled so the distinction from an active
 * campaign is unmistakable.
 */
export function ArchivedCampaignCard({ campaign }: ArchivedCampaignCardProps) {
  const characterNames = campaign.assignedCharacters
    .map((c) => c.characterName)
    .join(", ");

  return (
    <article
      aria-label={campaign.name}
      className={cn(CARD_CLASSNAME, "flex h-full flex-col bg-muted/50")}
    >
      <CardHeader className="space-y-1.5 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-base text-muted-foreground leading-tight">
            {campaign.name}
          </h3>
          <Badge variant="outline">Archived</Badge>
        </div>
        {campaign.archivedAt && (
          <p className="text-muted-foreground text-xs">
            Archived {campaign.archivedAt.toLocaleDateString()}
          </p>
        )}
        <p className="text-muted-foreground text-sm">
          {characterNames || "No characters assigned"}
        </p>
      </CardHeader>
    </article>
  );
}
